import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import adminApi from "../lib/adminApi";

const POLL_MS = 20000;

function getCtx() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!window.__haAudio) window.__haAudio = new Ctx();
  return window.__haAudio;
}

function beep() {
  try {
    const ctx = getCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;
    [880, 1320].forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = f;
      o.connect(g);
      g.connect(ctx.destination);
      const t = now + i * 0.18;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.3, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.16);
      o.start(t);
      o.stop(t + 0.18);
    });
  } catch {
    /* ignore */
  }
}

// Polls the admin orders endpoint and raises an alert (sound + toast + browser
// notification when the tab is backgrounded) whenever a brand-new order appears.
// Closed-app push notifications require server-side Web Push (see MOBILE_BACKEND_SPEC.md).
export function useOrderAlerts({ enabled, labels = {} }) {
  const [unseen, setUnseen] = useState(0);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("ha_admin_sound") !== "0");
  const [notifPerm, setNotifPerm] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const knownRef = useRef(null); // Set of seen order_ids; null = not seeded yet
  const soundRef = useRef(soundOn);
  const labelsRef = useRef(labels);
  soundRef.current = soundOn;
  labelsRef.current = labels;

  useEffect(() => {
    localStorage.setItem("ha_admin_sound", soundOn ? "1" : "0");
  }, [soundOn]);

  const requestNotif = useCallback(async () => {
    // Resume the audio context inside this user gesture so beeps work later.
    try {
      const ctx = getCtx();
      if (ctx && ctx.state === "suspended") await ctx.resume();
    } catch {
      /* ignore */
    }
    if (typeof Notification === "undefined") return;
    try {
      const p = await Notification.requestPermission();
      setNotifPerm(p);
    } catch {
      /* ignore */
    }
  }, []);

  const poll = useCallback(async () => {
    try {
      const data = await adminApi.orders("");
      const list = Array.isArray(data) ? data : data?.orders || [];
      const ids = list.map((o) => o.order_id).filter(Boolean);
      if (knownRef.current === null) {
        knownRef.current = new Set(ids);
        return;
      }
      const fresh = list.filter((o) => o.order_id && !knownRef.current.has(o.order_id));
      fresh.forEach((o) => knownRef.current.add(o.order_id));
      if (!fresh.length) return;

      setUnseen((n) => n + fresh.length);
      if (soundRef.current) beep();

      const l = labelsRef.current;
      const orderLabel = fresh[0].order_number || fresh[0].order_id;
      const title = fresh.length > 1 ? `${fresh.length} ${l.newOrders || "new orders"}` : l.newOrder || "New order";
      toast.success(`${title} · ${orderLabel}`);

      if (typeof Notification !== "undefined" && Notification.permission === "granted" && document.hidden) {
        try {
          const n = new Notification(`${title} 🛎️`, { body: String(orderLabel), tag: "ha-order", renotify: true });
          n.onclick = () => {
            window.focus();
            n.close();
          };
        } catch {
          /* ignore */
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => clearInterval(id);
  }, [enabled, poll]);

  const clearUnseen = useCallback(() => setUnseen(0), []);
  return { unseen, clearUnseen, soundOn, setSoundOn, notifPerm, requestNotif };
}

export default useOrderAlerts;
